<template>
  <v-container fluid>
    <v-row>
      <!-- Location tabs -->
      <v-col cols="12">
        <v-tabs
          v-model="tab"
          center-active
          bg-color="white"
          color="black"
          align-tabs="start"
          show-arrows
        >
          <v-tab
            v-for="tab of tabs"
            :key="tab.value"
            :value="tab.value"
            :disabled="tab.disabled"
          >
            <v-icon class="mr-2" size="small">{{ tab.icon }}</v-icon>
            {{ tab.title }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>
    <v-tabs-window v-model="tab" :touch="!(editGeofence || editGeolocation)">
      <!-- LOCATION -->
      <v-tabs-window-item value="location">
        <v-card flat border class="mt-2">
          <v-card-title>
            {{ $t("machines.location.location.title") }}
          </v-card-title>
          <v-card-subtitle class="pb-0">
            <p>{{ $t("machines.location.location.description") }}</p>
            <p v-if="geofencingSupported">
              {{
                $t(
                  "machines.location.location.messages.changeGeofencingCoordinatesTip"
                )
              }}
            </p>
          </v-card-subtitle>
          <v-card-text>
            <v-row
              v-if="canAddGeofence && geofencingSupported && showAddGeofence"
            >
              <v-col cols="12">
                <v-btn
                  color="secondary"
                  class="text-accent"
                  @click="addDefaultGeofenceArea"
                  :disabled="!currentMachineLocalization"
                >
                  <v-icon start>mdi-plus-circle</v-icon>
                  {{ $t("machines.location.geofencingSettings.actions.add") }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="pt-0" cols="12" v-if="geofenceSettingsNotSyched">
                <v-alert
                  closable
                  class="mb-0 alert-message"
                  elevation="2"
                  border="start"
                  density="compact"
                  type="warning"
                >
                  {{ $t("machines.messages.machineOffline") }}
                  {{
                    $t(
                      "machines.location.geofencingSettings.messages.settingsNotSynched"
                    )
                  }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col :md="canReadGeofence ? 8 : 12" cols="12" class="pr-2">
                <LocationMapView
                  :machine="machine"
                  :showHistory="showHistory"
                  :historyDates="historyDates"
                />
              </v-col>
              <v-col md="4" cols="12">
                <GeofencingSettings
                  :machine="this.machine"
                  v-if="canReadGeofence"
                  v-model:editGeofence="editGeofence"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col :md="canReadGeofence ? 8 : 12" cols="12" class="pr-2">
                <v-checkbox
                  class="ma-0 pa-0 grab-location-show-history"
                  hide-details
                  v-model="showHistory"
                  :label="$t('machines.location.location.showHistory')"
                ></v-checkbox>
                <TimeRange
                  v-show="showHistory"
                  :canEdit="showHistory"
                  v-model:return-value="historyDates"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tabs-window-item>
      <!-- GEOLOCATION SETTINGS -->
      <v-tabs-window-item value="geolocation">
        <GeolocationSettings
          :machine="this.machine"
          v-model:editGeolocation="editGeolocation"
        />
      </v-tabs-window-item>
      <!-- EVENTS -->
      <v-tabs-window-item value="events">
        <LocationEvents :machine="this.machine" />
      </v-tabs-window-item>
    </v-tabs-window>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex"
import LocationMapView from "@/views/machine/LocationMapView"
import GeolocationSettings from "@/views/machine/GeolocationSettings"
import GeofencingSettings from "@/views/machine/GeofencingSettings"
import LocationEvents from "@/views/machine/LocationEvents"
import TimeRange from "@/components/filters/TimeRange"

export default {
  name: "Location",
  props: ["machine"],
  components: {
    LocationMapView,
    GeolocationSettings,
    GeofencingSettings,
    LocationEvents,
    TimeRange
  },
  data() {
    return {
      tab: "location",

      // Owned here (rather than in LocationMapView) so the map card and the
      // geofence card can each be the sole item in their column and match
      // heights; the history checkbox/date-range sits in its own row below.
      showHistory: false,
      historyDates: [],

      editGeolocation: false,
      editGeofence: false,

      // Events
      locationEvents: [],

      // Notification stuff
      notification: null,

      recipients: [],

      isSendingNotification: false,

      notificationHistory: []
    }
  },
  computed: {
    ...mapGetters([
      "canReadGeolocation",
      "canReadGeofence",
      "canAddGeofence",
      "currentMachineLocalization"
    ]),

    geofenceSettingsNotSyched() {
      const settings = this.$store.getters.machineGeofencingSettings
      if (settings) {
        if (settings.desiredOptions) {
          if (settings.reportedOptions) {
            if (
              JSON.stringify(settings.desiredOptions) !=
              JSON.stringify(settings.reportedOptions)
            ) {
              return true
            }
          } else {
            return true
          }
        }
      }
      return false
    },
    tabs() {
      let items = []
      items.push({
        value: "location",
        icon: "mdi-map-marker-radius",
        title: this.$t("machines.tabHeaders.location"),
        disabled: this.editGeolocation
      })
      /* Settings tab will only be visible to users with admin role /*
      /* All others will have no access at all */
      if (this.$store.getters.canReadGeolocation) {
        items.push({
          value: "geolocation",
          icon: "mdi-cogs",
          title: this.$t("machines.tabHeaders.settings"),
          disabled: this.editGeofence
        })
      }
      items.push({
        value: "events",
        icon: "mdi-bell-outline",
        title: this.$t("machines.tabHeaders.events"),
        disabled: this.editGeofence || this.editGeolocation
      })
      return items
    },
    geofencingSupported() {
      return this.$store.getters.machineGeofencingSettings?.capabilities
        ?.notifications
    },
    showAddGeofence() {
      return (
        !this.$store?.getters?.geofenceAreaLatitude &&
        !this.$store?.getters?.geofenceAreaEditMode
      )
    },
    editModeEnabled: {
      get() {
        return this.$store.getters.geofenceAreaEditMode
      },
      set(val) {
        this.$store.dispatch("setGeofenceAreaEditMode", val)
      }
    }
  },
  watch: {},
  methods: {
    addDefaultGeofenceArea() {
      if (this.currentMachineLocalization) {
        this.$store.dispatch("updateGeofenceArea", {
          latitude: this.currentMachineLocalization.lat,
          longitude: this.currentMachineLocalization.lng,
          radius: 300 /*in meters*/,
          unit: "km"
        })
        this.editModeEnabled = true
      }
    }
  }
}
</script>
<style></style>
